import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Trash2, Users, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const ROLE_LABEL = {
  superadmin: "Superadmin",
  admin: "Admin",
  marketing: "Marketing",
  finance: "Finance",
  staff_cs: "Staff CS",
};

const ROLE_DESC = {
  admin: "Akses penuh kelola paket, jamaah, booking, konten & broadcast",
  marketing: "Broadcast WA, kelola blog, testimoni & galeri",
  finance: "Verifikasi pembayaran, export manifest & laporan keuangan",
  staff_cs: "Baca-only bookings & pesan kontak (customer service)",
};

const ROLE_COLOR = {
  superadmin: "bg-primary text-white",
  admin: "bg-accent text-primary",
  marketing: "bg-pink-100 text-pink-800",
  finance: "bg-emerald-100 text-emerald-800",
  staff_cs: "bg-blue-100 text-blue-800",
};

export default function StaffManagement() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setItems(data.items || []);
      setRoles(data.roles || []);
    } catch { toast.error("Gagal memuat daftar staf"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) return;
    setPosting(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      toast.success(data.created ? `Staf baru ditambahkan: ${form.email}` : `Peran diperbarui: ${form.email}`);
      setForm({ email: "", name: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengundang");
    } finally { setPosting(false); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/admin/staff/${uid}/role`, { role });
      toast.success(`Peran diubah ke ${ROLE_LABEL[role] || role}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal ubah peran"); }
  };

  const revoke = async (uid, email) => {
    if (!window.confirm(`Cabut akses staf ${email}?`)) return;
    try {
      await api.delete(`/admin/staff/${uid}`);
      toast.success("Akses dicabut");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mencabut"); }
  };

  const assignableRoles = roles.filter((r) => r !== "superadmin");

  return (
    <div data-testid="staff-management-page" className="max-w-4xl space-y-8">
      <div>
        <h2 className="font-serif text-3xl text-primary flex items-center gap-2">
          <Users className="h-6 w-6 text-accent" /> Kelola Staf & Peran
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Undang staf dengan peran terbatas. Superadmin (pemilik) tidak bisa diubah/dihapus.
        </p>
      </div>

      {/* Role reference */}
      <div className="bg-card border border-border rounded-lg p-6">
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">Referensi Peran</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {assignableRoles.map((r) => (
            <div key={r} className="border border-border rounded-md p-3">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${ROLE_COLOR[r] || "bg-muted"}`}>{ROLE_LABEL[r]}</span>
              <p className="text-xs text-muted-foreground mt-2">{ROLE_DESC[r]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invite form */}
      <form onSubmit={invite} className="bg-card border border-border rounded-lg p-6" data-testid="invite-form">
        <p className="font-medium text-primary flex items-center gap-2"><UserPlus className="h-4 w-4 text-accent" /> Undang Staf Baru</p>
        <div className="mt-4 grid sm:grid-cols-3 gap-3">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="email@example.com"
            required
            data-testid="invite-email"
          />
          <Input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nama (opsional)"
            data-testid="invite-name"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="border border-border rounded-md px-3 py-2 text-sm bg-background"
            data-testid="invite-role"
          >
            {assignableRoles.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
        </div>
        <div className="mt-4 flex justify-end">
          <Button type="submit" disabled={posting || !form.email.trim()} data-testid="invite-submit">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Undang"}
          </Button>
        </div>
      </form>

      {/* Staff list */}
      <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="staff-list">
        <div className="p-6 border-b border-border">
          <p className="font-medium text-primary flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-accent" /> Daftar Staf ({items.length})
          </p>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-primary">
            <tr>
              <th className="text-left p-3 font-medium">Nama</th>
              <th className="text-left p-3 font-medium">Email</th>
              <th className="text-left p-3 font-medium">Peran</th>
              <th className="text-right p-3 font-medium">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">Belum ada staf.</td></tr>}
            {!loading && items.map((s) => {
              const isSuper = s.role === "superadmin";
              return (
                <tr key={s.user_id} className="border-t border-border" data-testid={`staff-row-${s.user_id}`}>
                  <td className="p-3 text-primary font-medium">{s.name || "-"}</td>
                  <td className="p-3 text-muted-foreground">{s.email}</td>
                  <td className="p-3">
                    {isSuper ? (
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${ROLE_COLOR.superadmin}`}>Superadmin</span>
                    ) : (
                      <select
                        value={s.role || "admin"}
                        onChange={(e) => changeRole(s.user_id, e.target.value)}
                        className="border border-border rounded px-2 py-1 text-xs bg-background"
                        data-testid={`staff-role-${s.user_id}`}
                      >
                        {assignableRoles.map((r) => (
                          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {!isSuper && (
                      <button
                        onClick={() => revoke(s.user_id, s.email)}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                        data-testid={`staff-revoke-${s.user_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Cabut
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
